import axios from 'axios';
import { configModel } from './app_config_model';

/**
 * Ensure the URL is valid, otherwise throw an Error.
 *
 * @param {String} url - The URL to validate.
 * @throws {Error} When URL is invalid.
 */
const _validateUrl = (url) => {
    if (!url || typeof url !== 'string') {
        throw Error('Invalid URL passed!');
    }
};

/**
 * Get some extra information out of 'Network Error'.
 *
 * @param {Object} error - Object being created and thrown.
 * @throws {Error} When Error Message is equal to 'Network Error' show extra.
 */
const _throwError = (error) => {
    if (error.message === 'Network Error') {
        if (window.Raven) {
            window.Raven.captureException(error, {
                requestUrl: error.config.url
            });
        }
    }
    throw error; // upstream is not using promise directly, so we need to re-throw explicitly.
};
/**
 * Generic XHR GET request with a default timeout.
 *
 * @param {String} url - The URL to call.
 * @param {Object} [config] - Optional config to pass to the request, see https://github.com/mzabriskie/axios#request-config.
 * @returns {Promise} A promise that will resolve when a response has been received from the backend.
 */
const _makeGetRequest = (url, config = {}) => {
    _validateUrl(url);
    return axios.get(url, { timeout: 10000, ...config });
};

/**
 * Generic XHR POST request with a default timeout.
 *
 * @param {String} url - The URL to call.
 * @param {Object} data - Data to send with request.
 * @param {Object} [config] - Optional config to pass to the request, see https://github.com/mzabriskie/axios#request-config.
 * @returns {Promise} A promise that will resolve when a response has been received from the backend.
 */
const _makePostRequest = (url, data, config = {}) => {
    _validateUrl(url);
    // @todo: the timeout needs to be config able for different env and vertical, change to 45 secs to cater for shitty hnc providers
    return axios.post(url, data, { timeout: 45000, ...config });
};

/**
 * Return an object that maps header names to header values.
 *
 * @param {String} accessToken - Auth token required by API.
 * @param {String} sessionToken - Customer account session token
 * @returns {Object} The headers to use in the request.
 * @throws {Error} When accessToken is invalid.
 */
const _getAuthHeaders = (accessToken, sessionToken) => {
    if (!accessToken || typeof accessToken !== 'string') {
        throw Error(`Invalid accessToken passed! (${accessToken})`);
    }

    if (sessionToken) {
        return {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'x-ctm-authorization': `Bearer ${sessionToken}`
        };
    }

    return {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
    };
};

/**
 * Make an authorized GET request to the backend for a payload described in action.
 *
 * @param {String} url - The URL to call.
 * @param {String} accessToken - Access token required by API.
 * @param {String} sessionToken - Customer account session token
 * @returns {Promise} A promise that will resolve when a response has been received from the backend.
 * @throws {Error} When XHR request failed.
 */
export const getRequest = (url, accessToken, sessionToken) => {
    const config = {};
    try {
        config.headers = _getAuthHeaders(accessToken, sessionToken);
    } catch (e) {
        throw new Error(`${e}, getRequest:${url}`);
    }

    return _makeGetRequest(url, config)
        .then((response) => response)
        .catch((error) => _throwError(error));
};

/**
 * Make an authorized POST request to the backend for a payload described in action.
 *
 * @param {String} url - The URL to call.
 * @param {String} accessToken - Access token required by API.
 * @param {String} sessionToken - Customer account session token
 * @param {Object} data - Data to send with request.
 * @returns {Promise} A promise that will resolve when a response has been received from the backend.
 * @throws {Error} When XHR request failed.
 */
export const postRequest = (url, accessToken, sessionToken, data) => {
    const config = {};
    try {
        config.headers = _getAuthHeaders(accessToken, sessionToken);
    } catch (e) {
        throw new Error(`${e}, postRequest:${url}`);
    }

    return _makePostRequest(url, data, config)
        .then((response) => response)
        .catch((error) => _throwError(error));
};

/**
 * Make a request to the backend for the authorization payload, example:
 *   - access_token
 *   - expires_in
 *   - jti
 *   - refresh_token
 *   - scope
 *   - token_type
 *
 * @param {String} url - The URL to call.
 * @param {Object} refreshToken - Refresh token passed to the API.
 * @returns {Promise} A promise that will resolve when a response has been received from the backend.
 * @throws {Error} When refreshToken is invalid.
 * @throws {Error} When XHR request failed.
 */
export const getAuthTokens = (url, refreshToken = null) => {
    if (refreshToken) {
        if (typeof refreshToken !== 'string') {
            throw Error('Valid refresh token required!');
        }
        const data = { refresh_token: refreshToken };
        return _makePostRequest(url, data)
            .then((response) => response.data)
            .catch((error) => _throwError(error));
    }
    return _makeGetRequest(url)
        .then((response) => response.data)
        .catch((error) => _throwError(error));
};

/**
 * Make an POST request to the error endpoint.
 *
 * @param  {Object} data - Data to send with request.
 * @returns {Promise} A promise that will resolve when a response has been received from the backend.
 * @throws {Error} When vertical is invalid.
 */
export const postReporter = (data) => {
    const url = `/${configModel.getNodeEndpointBaseUrl()}/reporter`;
    return _makePostRequest(url, data)
        .catch((error) => _throwError(error));
};
